import React from 'react';
import { Box } from '@mui/material';
import { Line as ChartLine } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

interface CombinedTripsPerHourChartProps {
  regularTripsData: { hour: string; count: number }[];
  frodoTripsData: { hour: string; count: number }[];
}

const CombinedTripsPerHourChart: React.FC<CombinedTripsPerHourChartProps> = ({
  regularTripsData,
  frodoTripsData
}) => {
  const { t } = useTranslation('simulation');
  // Extract only the time portion from the hour string (e.g., "2024-01-15 17:00" -> "17:00")
  const formatTimeLabel = (hourString: string): string => {
    if (!hourString) return '';
    // If the string contains a space, take the part after the space
    const parts = hourString.split(' ');
    if (parts.length > 1) {
      return parts[parts.length - 1]; // Return the last part (time)
    }
    // If it's already just time, return as is
    return hourString;
  };

  // Combine both datasets to get all unique hours
  const allHours = new Set<string>();
  regularTripsData.forEach(d => allHours.add(d.hour));
  frodoTripsData.forEach(d => allHours.add(d.hour));
  const sortedHours = Array.from(allHours).sort();

  // Create data arrays aligned to the same hours
  const regularCounts = sortedHours.map(hour => {
    const dataPoint = regularTripsData.find(d => d.hour === hour);
    return dataPoint ? dataPoint.count : 0;
  });

  const frodoCounts = sortedHours.map(hour => {
    const dataPoint = frodoTripsData.find(d => d.hour === hour);
    return dataPoint ? dataPoint.count : 0;
  });

  const chartData = {
    labels: sortedHours.map(formatTimeLabel),
    datasets: [
      {
        label: t('results.charts.onDemandTrips'),
        data: regularCounts,
        fill: false,
        borderColor: '#1976d2',
        backgroundColor: '#1976d2',
        tension: 0.2,
        pointRadius: 2,
      },
      {
        label: t('results.charts.frodoOffloaded'),
        data: frodoCounts,
        fill: false,
        borderColor: '#FF9800',
        backgroundColor: '#FF9800',
        tension: 0.2,
        pointRadius: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: true,
        position: 'top' as const,
      },
      title: { display: false },
      tooltip: { 
        mode: 'index' as const, 
        intersect: false,
        callbacks: {
          title: (tooltipItems: any[]) => {
            // Show full date-time in tooltip
            if (tooltipItems.length > 0) {
              const index = tooltipItems[0].dataIndex;
              return sortedHours[index] || '';
            }
            return '';
          }
        }
      },
    },
    scales: {
      x: { title: { display: true, text: t('results.charts.hourAxis') }, ticks: { maxRotation: 45, minRotation: 45 } },
      y: { title: { display: true, text: t('results.charts.requestsAxis') }, beginAtZero: true },
    },
  } as const;

  return (
    <Box sx={{ width: 420, height: 300, background: 'white', borderRadius: 2, p: 1 }}>
      <ChartLine data={chartData} options={options} />
    </Box>
  );
};

export default CombinedTripsPerHourChart;

